<script setup lang="ts">
import { Quiz } from '@/models/Quiz';
import { progress } from '@/composables/progress';

const props = defineProps<{
  quiz: Quiz
}>();

const emit = defineEmits<{
  (e: 'setQuiz', id: string): void
}>();

const setQuiz = (id: string) => {
  if (props.quiz.comingSoon) {
    return;
  }

  emit('setQuiz', id);
};
</script>


<template>
  <div
      class="quiz-tile"
      :class="{ 'quiz-tile--coming-soon': props.quiz.comingSoon }"
      v-on:click="setQuiz(props.quiz.id)"
      v-on:keyup.enter.space="setQuiz(props.quiz.id)"
      tabIndex="0"
      :aria-disabled="props.quiz.comingSoon"
      role="button"
      :title="props.quiz.comingSoon ? 'Demnächst verfügbar' : ''"
  >
    <div class="quiz-tile__title">
      <h2>{{ props.quiz.name }}</h2>
    </div>
    <div class="quiz-tile__description">
      <p>{{ props.quiz.description }}</p>
    </div>
    <div class="quiz-tile__score" v-if="props.quiz.completed && props.quiz.score">
      <code class="dive-code">
        {{ Math.round(props.quiz.score) }}%
      </code>
    </div>
    <div v-if="!props.quiz.comingSoon" class="quiz-tile__progress" :title="'Fortschritt: ' + progress(quiz) + '%'">
      <div :class="[progress(quiz) === 100 ? 'quiz-tile__progress--bar--completed' : '', 'quiz-tile__progress--bar']" :style="{ width: progress(quiz) + '%'}"></div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/scss/colors.scss';
@import '@/assets/scss/layout.scss';
@import '@/assets/scss/typography.scss';

a {
  color: $color-primary-400;
  font-weight: 700;
}

.quiz-tile {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  max-width: 100%;
  background: $color-secondary-400;
  cursor: pointer;
  transition: $transition-default;
  position: relative;

  &:hover {
    box-shadow: $box-shadow-default;
  }

  &--coming-soon {
    filter: grayscale(100%);
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      box-shadow: 0 0;
    }
  }

  &:focus-visible {
    outline: $outline;
    transition: none;
  }

  div {
    text-align: center;
    color: $color-light;
  }

  .quiz-tile__title {
    position: relative;
    background: $color-primary-400;
    color: $color-secondary-400;
    line-height: $line-height-narrow;
    padding: 1.5em;

    h2 {
      margin: 0;
      font-size: $font-size-larger;
    }
  }

  .quiz-tile__description {
    text-align: center;
    text-decoration: underline;
    text-decoration-color: $color-primary-400;
    font-weight: $font-weight-medium;
    padding: 2em 1em;
    line-height: $line-height-default;
  }

  .quiz-tile__score {
    position: absolute;
    right: -1.75em;
    top: -1.25em;
    width: 4.5em;
    height: 4.5em;
    background: $color-green-dark;
    border-radius: 50%;
    color: $color-light;
    font-weight: $font-weight-medium;
    font-size: $font-size-smallest;
    letter-spacing: 1px;
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 1;
    border: 2px solid white;
    box-shadow: $box-shadow-default;
  }

  .quiz-tile__progress {
    position: absolute;
    display: block;
    bottom: -6px;
    background: $color-gray-300;
    height: 6px;
    width: 100%;
    z-index: 2;

    &--bar {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      background: $color-gray-500;

      &--completed {
        background: $color-green-dark;
        opacity: 0.5;
      }
    }
  }
}
</style>